import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import { Check, ChevronRight, Briefcase, Zap, Clock3, Plus, Sparkles, MoonStar } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';

const COLORS = ['#B4233A', '#2563EB', '#15803D', '#C26A1B', '#0F766E', '#4338CA'];

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [businesses, setBusinesses] = useState([
    { name: '', shortCode: '', color: '#B4233A' },
    { name: '', shortCode: '', color: '#2563EB' },
  ]);
  const [loading, setLoading] = useState(false);
  const { updateUser, user } = useAuth();
  const { createBusiness } = useBusiness();
  const { toggleTheme } = useTheme();
  const navigate = useNavigate();

  const updateBiz = (index, field, value) => {
    setBusinesses((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'name' && !next[index].shortCode) {
        next[index].shortCode = value.replace(/[^A-Za-z]/g, '').slice(0, 6);
      }
      return next;
    });
  };

  const addBusiness = () => {
    if (businesses.length < 5) {
      setBusinesses((prev) => [...prev, { name: '', shortCode: '', color: COLORS[prev.length % COLORS.length] }]);
    }
  };

  const removeBusiness = (index) => {
    if (businesses.length > 1) {
      setBusinesses((prev) => prev.filter((_, idx) => idx !== index));
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      const validBusinesses = businesses.filter((biz) => biz.name.trim());
      for (const biz of validBusinesses) {
        await createBusiness({
          name: biz.name,
          shortCode: biz.shortCode || biz.name.slice(0, 4),
          color: biz.color,
          icon: 'briefcase',
        });
      }
      const api = (await import('../api')).default;
      await api.put('/auth/onboard');
      updateUser({ onboarded: true });
      navigate('/');
    } catch (err) {
      toast.error('Onboarding failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: 'Name the ventures you manage',
      subtitle: 'Each business gets its own lane, accent, and filtering logic across the desk.',
      icon: Briefcase,
    },
    {
      title: 'Decide your operating rhythm',
      subtitle: 'We surface planning and review rituals so the desk behaves like a calm assistant.',
      icon: Clock3,
    },
    {
      title: 'Keep room for automation',
      subtitle: 'Command bar, AI parsing, and integrations can expand later without changing your operating model.',
      icon: Zap,
    },
  ];

  return (
    <div className="min-h-screen px-4 py-4 md:px-6 md:py-6" data-testid="onboarding-page" style={{ background: 'var(--pd-bg)' }}>
      <div className="relative min-h-[calc(100vh-2rem)] overflow-hidden rounded-[36px] border pd-glass" style={{ borderColor: 'var(--pd-shell-line)' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'var(--pd-hero-wash)' }} />
        <div className="relative z-10 grid min-h-[calc(100vh-2rem)] gap-6 lg:grid-cols-[360px_minmax(0,1fr)] p-5 md:p-7 lg:p-8">
          <div className="rounded-[32px] p-6 flex flex-col" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}>
            <span className="assistant-chip w-fit">WELCOME, {user?.name?.split(' ')[0]?.toUpperCase() || 'OPERATOR'}</span>
            <h1 className="text-3xl md:text-4xl font-medium mt-6" style={{ color: 'var(--pd-text)', letterSpacing: '-0.05em' }}>Let’s align the desk to your real workload.</h1>
            <p className="text-[14px] mt-4" style={{ color: 'var(--pd-muted)', lineHeight: 1.8 }}>This setup turns PolkDesQ into a product that behaves like both a seasoned secretary and a productivity coach.</p>

            <div className="grid gap-3 mt-8">
              {steps.map((item, index) => {
                const active = index === step;
                const complete = index < step;
                return (
                  <div key={item.title} data-testid={`onboarding-step-card-${index}`} className="rounded-[24px] px-4 py-4" style={{ background: active ? 'var(--pd-panel-2)' : 'var(--pd-panel)', border: `1px solid ${active ? 'var(--pd-border)' : 'var(--pd-border-subtle)'}` }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: active ? 'var(--pd-panel)' : 'var(--pd-panel-2)', color: active ? 'var(--pd-focus)' : 'var(--pd-muted)' }}>
                        {complete ? <Check className="w-4.5 h-4.5" /> : <item.icon className="w-4.5 h-4.5" />}
                      </div>
                      <div>
                        <div className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>{item.title}</div>
                        <div className="text-[11px] mt-1" style={{ color: 'var(--pd-subtle)' }}>{item.subtitle}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button data-testid="onboarding-theme-toggle" onClick={toggleTheme} className="mt-auto w-full flex items-center justify-between px-4 py-3 rounded-[24px] btn-ghost">
              <span>Need a darker room? Switch theme.</span>
              <MoonStar className="w-4 h-4" />
            </button>
          </div>

          <div className="rounded-[32px] p-6 md:p-8 flex flex-col" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', boxShadow: 'var(--pd-shadow-2)' }}>
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>Setup step {step + 1} of {steps.length}</div>
                <h2 className="text-[28px] font-medium mt-3" style={{ color: 'var(--pd-text)', letterSpacing: '-0.04em' }}>{steps[step].title}</h2>
              </div>
              <Sparkles className="w-5 h-5" style={{ color: 'var(--pd-focus)' }} />
            </div>

            <div className="flex gap-2 mb-8">
              {steps.map((_, index) => (
                <div key={`step-${index}`} className="h-1.5 flex-1 rounded-full" style={{ background: index <= step ? 'var(--pd-focus)' : 'var(--pd-border)' }} />
              ))}
            </div>

            {step === 0 ? (
              <div className="space-y-4">
                {businesses.map((biz, index) => (
                  <div key={biz.name || `biz-${index}`} className="rounded-[24px] p-4" style={{ background: 'var(--pd-panel-solid)', border: '1px solid var(--pd-border)' }}>
                    <div className="flex items-start gap-4">
                      <div className="flex gap-2 flex-wrap pt-1">
                        {COLORS.map((color) => (
                          <button key={color} data-testid={`onboarding-color-${index}-${color.replace('#', '')}`} onClick={() => updateBiz(index, 'color', color)} className="w-7 h-7 rounded-xl" style={{ background: color, outline: biz.color === color ? '2px solid var(--pd-text)' : 'none', outlineOffset: 2 }} />
                        ))}
                      </div>
                      <div className="flex-1 grid gap-3 md:grid-cols-2">
                        <input data-testid={`onboarding-biz-name-${index}`} placeholder="Business name" value={biz.name} onChange={(e) => updateBiz(index, 'name', e.target.value)} className="w-full px-4 py-3 rounded-[18px] text-sm outline-none" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel)' }} />
                        <input data-testid={`onboarding-biz-code-${index}`} placeholder="Short code" value={biz.shortCode} onChange={(e) => updateBiz(index, 'shortCode', e.target.value)} className="w-full px-4 py-3 rounded-[18px] text-sm outline-none" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel)' }} maxLength={6} />
                      </div>
                      {businesses.length > 1 ? <button data-testid={`onboarding-remove-biz-${index}`} onClick={() => removeBusiness(index)} className="text-[11px] mt-2" style={{ color: 'var(--pd-subtle)' }}>Remove</button> : null}
                    </div>
                  </div>
                ))}
                {businesses.length < 5 ? <button data-testid="onboarding-add-business" onClick={addBusiness} className="w-full py-3 rounded-[20px] text-sm font-medium" style={{ border: '1px dashed var(--pd-border)', color: 'var(--pd-muted)' }}><Plus className="w-4 h-4 inline mr-1.5" /> Add another business</button> : null}
              </div>
            ) : step === 1 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {[
                  ['Morning planning', 'Every day begins with an operating brief and priority sort.'],
                  ['Evening review', 'Close the loop before tasks leak into tomorrow.'],
                  ['Weekly reset', 'See cross-business pressure before the next week starts.'],
                  ['Protected focus', 'Reserve deep work without losing visibility on urgent items.'],
                ].map(([title, copy]) => (
                  <div key={title} className="rounded-[24px] p-5" style={{ background: 'var(--pd-panel-solid)', border: '1px solid var(--pd-border)' }}>
                    <div className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>{title}</div>
                    <p className="text-[12px] mt-3" style={{ color: 'var(--pd-muted)', lineHeight: 1.7 }}>{copy}</p>
                    <span className="assistant-chip mt-4">Enabled by default</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {[
                  ['Command bar', 'Turn natural language into tasks, calls, and routing actions.'],
                  ['Calendar syncing', 'Prepare space for real integrations in a future phase.'],
                  ['Inbox capture', 'Bring requests from every channel into one triage lane.'],
                  ['Review coaching', 'Surface gentle prompts when execution drifts.'],
                ].map(([title, copy]) => (
                  <div key={title} className="rounded-[24px] p-5" style={{ background: 'var(--pd-panel-solid)', border: '1px solid var(--pd-border)' }}>
                    <div className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>{title}</div>
                    <p className="text-[12px] mt-3" style={{ color: 'var(--pd-muted)', lineHeight: 1.7 }}>{copy}</p>
                    <span className="assistant-chip mt-4">Ready for rollout</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between mt-10 gap-3">
              {step > 0 ? <button data-testid="onboarding-back-button" onClick={() => setStep((prev) => prev - 1)} className="px-5 py-3 rounded-[18px] btn-ghost">Back</button> : <div />}
              {step < steps.length - 1 ? (
                <button data-testid="onboarding-next-button" onClick={() => setStep((prev) => prev + 1)} className="flex items-center gap-2 px-5 py-3 rounded-[18px] text-sm font-semibold text-white btn-primary">Continue <ChevronRight className="w-4 h-4" /></button>
              ) : (
                <button data-testid="onboarding-finish-button" onClick={handleFinish} disabled={loading} className="flex items-center gap-2 px-5 py-3 rounded-[18px] text-sm font-semibold text-white btn-primary">{loading ? 'Setting up...' : 'Launch my desk'} <Check className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
